import React, { useState, useEffect } from 'react';
import { Menu, X, Rocket } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const navLinks = [
    { name: t('nav.home'), href: '#hero' },
    { name: t('nav.features'), href: '#features' },
    { name: t('nav.comparison'), href: '#comparison' },
  ];

  const scrollToSection = (id: string) => {
    setIsOpen(false);
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'blur-bg border-b border-black/5 dark:border-white/5' : 'bg-transparent'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Rocket className="h-8 w-8 text-primary-light dark:text-primary" aria-hidden="true" />
            <span className="text-xl font-bold gradient-text ml-2">
              TBCC
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" role="navigation" aria-label="Main navigation">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-gray-600 dark:text-gray-300 hover:text-primary-light dark:hover:text-primary text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={`Navigate to ${link.name} section`}
              >
                {link.name}
              </button>
            ))}
            <button 
              className="gradient-bg px-6 py-2.5 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-lg shadow-primary/20 dark:shadow-primary/10 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Launch application"
            >
              Launch App
            </button>
            <ThemeToggle />
            <LanguageSwitcher />
          </nav>

          {/* Mobile Navigation Button */}
          <div className="md:hidden flex items-center space-x-4">
            <ThemeToggle />
            <LanguageSwitcher />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 dark:text-gray-300 hover:text-primary-light dark:hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              aria-expanded={isOpen}
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isOpen && (
        <div 
          className="md:hidden blur-bg border-t border-black/5 dark:border-white/5"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="px-4 py-2 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-primary-light dark:hover:text-primary text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={`Navigate to ${link.name} section`}
              >
                {link.name}
              </button>
            ))}
            <button 
              className="w-full gradient-bg px-4 py-2 rounded-lg text-white text-sm font-medium mt-4 shadow-lg shadow-primary/20 dark:shadow-primary/10 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Launch application"
            >
              Launch App
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;